import { useEffect, useId, useRef, useState } from "react";

export interface StaffRequestDraft {
  requestText: string;
  idempotencyKey: string;
}

/** The parent binds this composer to one patient/encounter; retries retain the request key. */
export function StaffRequestComposer({
  patientName,
  location,
  initialRequestText = "",
  disabled = false,
  active = true,
  onSubmit,
  onCancel,
}: {
  patientName: string;
  location?: string | undefined;
  initialRequestText?: string;
  disabled?: boolean;
  active?: boolean;
  onSubmit: (draft: StaffRequestDraft) => Promise<void>;
  onCancel: () => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);
  const [text, setText] = useState(initialRequestText);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = useRef(false);
  const key = useRef(crypto.randomUUID());
  const submittedText = useRef<string | null>(null);
  const busy = disabled || pending;
  return (
    <form
      className="tb-staff-composer"
      hidden={!active}
      aria-label={`Request staff for ${patientName}`}
      onSubmit={async (event) => {
        event.preventDefault();
        const requestText = text.trim();
        if (locked.current || disabled || !active || !requestText) return;
        submittedText.current = requestText;
        locked.current = true;
        setPending(true);
        setError(null);
        try {
          await onSubmit({ requestText, idempotencyKey: key.current });
          setText("");
          key.current = crypto.randomUUID();
          submittedText.current = null;
        } catch (cause) {
          setError(cause instanceof Error ? cause.message : "The staff request could not be sent.");
        } finally {
          locked.current = false;
          setPending(false);
        }
      }}
    >
      <p>
        <strong>{patientName}</strong>
        {location ? ` · ${location}` : ""}
      </p>
      <label htmlFor={inputId}>What does staff need to do?</label>
      <textarea
        id={inputId}
        ref={inputRef}
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={2000}
        rows={3}
        required
        disabled={busy || submittedText.current !== null}
        placeholder="For example: Please repeat the blood pressure before discharge."
      />
      <p className="tb-muted">This request stays in Needs staff until staff mark it done.</p>
      {error ? (
        <p className="tb-muted">
          Retry sends the same request safely. Its wording is kept until sending is confirmed.
        </p>
      ) : null}
      {error ? (
        <p className="tb-notice tb-notice-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="tb-room-actions">
        <button type="submit" className="tb-button tb-primary" disabled={busy || !text.trim()}>
          {pending ? "Sending…" : "Send staff request"}
        </button>
        <button type="button" className="tb-button" disabled={pending} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
